import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Button, Typography, Paper, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  fileToBase64check,
  FileToBase64encode,
} from "../../../utils/TkFileEncode";
import fields from "../../../assets/data/TkFileEncodeProperty";
import {
  TkFileUploadFileType,
  TkFileUploadProps,
} from "../../../type/componentsType/TkFileUpload";
import "./_style.scss";

/**
 * ファイルアップロード部品
 * ファイル名を引数にとって、結果コード、ファイル名、ファイルデータ(base64)を返す
 * @see TkfileUpload
 * @param multiple : 複数ファイルを扱うかどうか指定するprops
 * @param fileFormat : ファイルフォーマット
 * @param onChange : イベント
 * @param onRemove : ファイル削除時のイベント
 * @param defaultFilename : 親画面でファイル名を扱うために必要な更新用State変数
 * @param defaultfilebase64 : 親画面でbase64ファイルを扱うために必要な更新用State変数
 * @param setdefaultFilename : 親画面でファイル名を扱うために必要な更新用State関数
 * @param setdefaultfilebase64 : 親画面でbase64ファイル扱うために必要な更新用State関数
 * @param setmessageid : 親画面でメッセージバー部品に必要なid更新用State関数
 * @param disabled : ボタンの活性非活性を制御するためのprops
 * @param id : ファイル唯一の識別子
 * @return resultInfo : 結果コード(メッセージID)、ファイル名、ファイルデータ
 **/

const TkFileUpload: React.FC<TkFileUploadProps> = ({
  multiple,
  fileFormat,
  onChange,
  onRemove,
  onGetValues,
  wantToSetValue,
  disabled = false,
  id = 1,
  error,
  errorMsg,
  ...rest
}) => {
  const [selectedFileNameList, setSelectedFileNameList] = useState<string[]>(
    []
  );
  const [fileBase64List, setFileBase64List] = useState<TkFileUploadFileType[]>(
    []
  );

  useEffect(() => {
    if (wantToSetValue && wantToSetValue.length > 0) {
      setSelectedFileNameList(() => {
        let defValue: TkFileUploadFileType[] = [...wantToSetValue];

        if (!multiple) {
          defValue = [wantToSetValue[0]];
        }

        setFileBase64List(defValue);
        return [...defValue.map((item) => item.formName)];
      });

      const callOnChange = async () => {
        let defValue: TkFileUploadFileType[] = [...wantToSetValue];

        if (!multiple) {
          defValue = [wantToSetValue[0]];
        }

        await Promise.resolve();
        onChange && onChange({ target: { value: defValue, id } }, "change");
        onGetValues && onGetValues(defValue);
      };

      callOnChange();
    } else {
      setSelectedFileNameList([]);
      setFileBase64List([]);
      onChange && onChange({ target: { value: null, id } }, "change");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantToSetValue, multiple]);

  const fileRef = useRef(null);
  //ファイル選択時にダイアログに指定フォーマット以外選択できないように制御している。
  const acceptFileFormat =
    fileFormat && fileFormat.trim().length > 0
      ? fileFormat
      : fields.file.extension;

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    let returnedValueList: TkFileUploadFileType[] = [];

    const filePromises = Array.from(event.target.files).map(async (file) => {
      if (file) {
        //選択したファイルをbase64に変換した結果
        const resultErrMessage = fileToBase64check(file);
        if (resultErrMessage !== "") {
          // Do something
        }
        const encoderesult = await FileToBase64encode(file);
        returnedValueList = [
          ...returnedValueList,
          {
            formName: encoderesult.filename,
            fileBase64: encoderesult.base64result,
          },
        ];
      }
    });

    await Promise.all(filePromises);

    setSelectedFileNameList((prev) => {
      if (!multiple) {
        onChange &&
          onChange({ target: { value: [returnedValueList[0]], id } }, "change");
        setFileBase64List([returnedValueList[0]]);

        onGetValues &&
          onGetValues(
            { target: { value: [returnedValueList[0]], id } },
            "change"
          );

        return [returnedValueList[0].formName];
      }

      onChange &&
        onChange(
          { target: { value: [...fileBase64List, ...returnedValueList], id } },
          "change"
        );
      onGetValues &&
        onGetValues(
          { target: { value: [...fileBase64List, ...returnedValueList], id } },
          "change"
        );

      setFileBase64List([...fileBase64List, ...returnedValueList]);
      return [...prev, ...returnedValueList.map((item) => item.formName)];
    });
    // ファイル情報のリセット
    event.target.value = "";
    let dom = fileRef.current as any;
    if (dom) {
      dom.value = "";
    }
  };

  const handleRemoveFile = (id: string | number, selectedFileName: string) => {
    if (selectedFileName !== "") {
      const res = selectedFileNameList.filter(
        (item) => item !== selectedFileName
      );
      const fileRes = fileBase64List.filter(
        (item) => item.formName !== selectedFileName
      );

      setSelectedFileNameList(res);
      setFileBase64List(fileRes);
      onChange && onChange({ target: { value: fileRes, id } }, "remove");
      onGetValues && onGetValues({ target: { value: fileRes, id } }, "remove");
      onRemove && onRemove(id, selectedFileName);
    }
  };

  return (
    <Box className="tkFileUploadMain">
      <Paper elevation={2} className="tkFileUpload">
        {selectedFileNameList.length > 0 ? (
          <Box className={`divButton ${disabled && "disabled"}`}>
            {selectedFileNameList.map((item, index) => {
              return (
                <Box className="fileItem" key={`${item}${index}`}>
                  <Typography variant="body1" key={index}>
                    {item}
                  </Typography>
                  <Button
                    className="TkButton"
                    size="small"
                    onClick={() => handleRemoveFile(id, item)}
                    {...rest}
                  >
                    <DeleteIcon
                      data-testid="deleteIcon"
                      className="DeleteIcon"
                    />
                  </Button>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body1">ファイルを選択してください。</Typography>
        )}
        <Box className="input-hidden">
          <input
            data-testid="tkFileUploadInputForTest"
            type="file"
            ref={fileRef}
            accept={acceptFileFormat}
            multiple={multiple}
            style={{ display: "none" }}
            onChange={handleFileChange}
            id={`tk-file-upload-input${id}`}
            disabled={disabled}
          />
        </Box>
        <label htmlFor={`tk-file-upload-input${id}`}>
          <Button
            size="small"
            data-testid="tkFileUploadButtonForTest"
            component="span"
            disabled={disabled}
          >
            ファイルを選択
          </Button>
        </label>
      </Paper>

      {error && (
        <Typography className="errorTextInTkFileUpload" color="error">
          {errorMsg}
        </Typography>
      )}
    </Box>
  );
};

export default TkFileUpload;
